// The drawing editor: a placed part's own drawing edited on the canvas, in its
// local frame (inst.drawPolygons / drawWalls), entered from the part or from the
// product editor, and written back to the product's geometry. docs/claude/products.md.
import {
  type Bounds,
  type GeomEntity,
  type Geometry,
  type Polygon,
  type ProductInstance,
  type Vec2,
  type Wall,
} from '../../shared/types';
import { traitsOf } from '../../shared/categories';
import {
  enterEdit,
  flashToolStatus,
  invalidate,
  leaveEdit,
  newId,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import {
  getUiLang,
  tr,
} from '../i18n';
import { showConfirm } from '../dialog';
import { productName } from '../../shared/names';
import { setViewTab } from './sheetSpace';
import { curveTessellation } from '../curves';
import { type DrawingSnapshot } from '../drawingSnapshot';
import {
  centerGeometry,
  computeBounds,
  instanceToWorld,
  isFiniteBounds,
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import {
  type DrawingHolder,
  npDraft,
  renderEditor,
  withAlpha,
} from './productEditor';
import {
  Handle,
  shapeBBoxHandles,
} from '../render';
import { type UndoStash } from '../undoHistory';
import { wallBand } from '../wallTool';
import {
  $,
  ellipseParams,
  ellipsePoints,
  HANDLE_PX,
  restoreDrawing,
  setTool,
  snapshotDrawing,
  undoSteps,
  viewH,
  viewW,
  type ZOrderOp,
} from '../main';
import { polyContains } from '../hitRules';
import { clearSelection } from '../selection';

/** This module's state that main.ts reads and writes too. */
export const drawingEditor = {
  /** The placed part whose own drawing is being edited (the `part` edit mode,
   *  editMode.ts). The left panel is hidden; polygon/wall/line/circle tools write
   *  to inst.drawPolygons / inst.drawWalls (local frame) instead of the drawing. */
  get drawingEditId(): string | null { return state.editing?.kind === 'part' ? state.editing.id : null; },
  // The vertex being dragged (inside the drawing editor).
  drawEditVertexDrag: null as { id: string; ring: number; index: number } | null,
  /** A selected edge of the selected local polygon (Make horizontal / vertical):
   *  kept in the `part` mode with the editor's other picks (ui/core.ts). */
  get drawEditSelectedEdge(): { id: string; edge: number } | null { return state.drawEditSelectedEdge; },
  set drawEditSelectedEdge(e: { id: string; edge: number } | null) { state.drawEditSelectedEdge = e; },
  // When editing a product-modal part's drawing, the real drawing is snapshotted so
  // the temporary editor instance and its history can be discarded on exit, with the
  // edited geometry written back to the part.
  modalDrawEdit: null as { host: DrawEditHost; savedDrawing: DrawingSnapshot; savedSteps: UndoStash<string, DrawingSnapshot>; initial: string; view: { scale: number; tx: number; ty: number } } | null,
};

/** What opened a throwaway drawing-editor session (the product editor's part,
 *  a steam bench's profile): the session's instance is put on the canvas over
 *  the real drawing, which comes back untouched, its undo steps too, on exit. */
export interface DrawEditHost {
  /** Write the edited shapes back: an English reason they cannot be (the
   *  editor stays open, the reason flashed), or null. */
  finish(inst: ProductInstance): string | null;
  /** The drawing restored: show what opened the editor again. */
  reopen(): void;
  /** Its card at the top of the editor's panel, with its own way out (in place
   *  of Exit Drawing Editor). */
  panel?(body: HTMLElement, inst: ProductInstance): void;
  /** Only closed outlines can be drawn: the tools that draw open lines are
   *  greyed out (toolNeeds.ts). */
  closedOnly?: boolean;
  /** The banner over the canvas (translated): where the editor was opened
   *  from, what is edited, and a short hint. */
  banner?(inst: ProductInstance): EditBanner;
  /** Figures drawn on the canvas while editing (world points). */
  dims?(inst: ProductInstance): Array<{ a: Vec2; b: Vec2 }>;
  /** Once a frame while nothing is dragged: hold the shapes to the host's
   *  rules (true: something moved). */
  reconcile?(inst: ProductInstance): boolean;
}

/** The banner over the canvas while the drawing editor is open. */
export interface EditBanner { crumb?: string; title: string; hint: string }

/** The host of the throwaway session open now, if any. */
export function drawEditHost(): DrawEditHost | null {
  return drawingEditor.drawingEditId ? drawingEditor.modalDrawEdit?.host ?? null : null;
}

/** Open a throwaway drawing-editor session for `host` on `inst` (pushed onto
 *  the drawing, entered, the view fitted to `bounds`). */
export function openDrawEditSession(host: DrawEditHost, inst: ProductInstance, bounds: Bounds | null): void {
  if (state.viewTab !== 'model') setViewTab('model'); // the editor works on the plan's canvas
  drawingEditor.modalDrawEdit = { host, savedDrawing: snapshotDrawing(), savedSteps: undoSteps.stash(), initial: '', view: { scale: vp.scale, tx: vp.tx, ty: vp.ty } };
  state.drawing.instances.push(inst);
  enterDrawingEdit(inst.id);
  drawingEditor.modalDrawEdit.initial = shapesKey(inst); // after the entry made the part's drawing shapes
  syncDrawEditChrome(); // the catalogue bar folded now, so the fit below sees the canvas's real width
  if (bounds && isFiniteBounds(bounds)) vp.fit(bounds, viewW(), viewH());
  invalidate();
}

/** Once a frame, while nothing is dragged: the session's host holds its shapes
 *  to its rules (a profile stays on the floor against the wall). */
export function reconcileDrawEditHost(): void {
  // A drawing opened or started over the editor took its part with it: the
  // editor (and a session's host, as on Cancel) goes too, never left open on
  // nothing.
  if (drawingEditor.drawingEditId && !drawEditInst()) {
    const session = drawingEditor.modalDrawEdit;
    leaveEdit('part');
    drawingEditor.modalDrawEdit = null;
    drawingEditor.drawEditVertexDrag = null;
    syncDrawEditChrome();
    session?.host.reopen();
    renderProperties();
    invalidate();
    return;
  }
  // The mode left some other way while a session was open: the real drawing
  // comes back, as on Cancel, so the session's part is never stranded in it.
  if (drawingEditor.modalDrawEdit && !drawingEditor.drawingEditId) { closeDrawEditSession(false); return; }
  const host = drawEditHost(), inst = drawEditInst();
  if (host?.reconcile && inst && state.dragMode === 'none' && host.reconcile(inst)) { renderProperties(); invalidate(); }
}

/** Leave the throwaway session without writing anything back. */
export function cancelDrawEditSession(): void {
  if (drawingEditor.modalDrawEdit) closeDrawEditSession(false);
}

/** The editor instance's shapes, as text to compare. */
function shapesKey(inst: ProductInstance): string {
  return JSON.stringify([inst.drawPolygons ?? [], inst.drawWalls ?? []]);
}

/** Whether the throwaway session's shapes changed since it opened. */
export function drawEditChanged(): boolean {
  const s = drawingEditor.modalDrawEdit, inst = drawEditInst();
  return !!s && !!inst && shapesKey(inst) !== s.initial;
}

let cancelAsked = false;
/** Cancel, asked first when something was changed (Esc, the banner's Cancel,
 *  the panel's). A placed part's drawing (no session) is edited live and
 *  undoable: leaving it keeps it. */
export async function requestCancelDrawEdit(): Promise<void> {
  if (!drawingEditor.drawingEditId || cancelAsked) return;
  if (!drawingEditor.modalDrawEdit) { exitDrawingEdit(); return; }
  if (drawEditChanged()) {
    cancelAsked = true;
    const name = drawEditInst()?.name ?? '';
    const ok = await showConfirm(tr('Discard the changes made to {name}?').replace('{name}', name), { title: tr('Discard changes'), ok: tr('Discard'), danger: true });
    cancelAsked = false;
    if (!ok || !drawingEditor.modalDrawEdit) return;
  }
  cancelDrawEditSession();
}

/** The buttons of the banner over the canvas. */
export function wireDrawEditBanner(): void {
  $('edit-banner-done').addEventListener('click', () => exitDrawingEdit());
  $('edit-banner-cancel').addEventListener('click', () => { void requestCancelDrawEdit(); });
}

let chromeKey = '';
/** Once a frame: the window around the canvas says the drawing editor is open
 *  — the banner over the canvas with Done (and Cancel, for a session), the
 *  catalogue bar folded away, the view tabs locked — however the editor was
 *  entered or left. */
export function syncDrawEditChrome(): void {
  const inst = drawEditInst(), host = drawEditHost();
  const b: EditBanner | null = !inst ? null : host?.banner ? host.banner(inst)
    : { title: tr('Drawing Editor — {name}').replace('{name}', productName(inst, getUiLang())), hint: tr('Polygon, rectangle, circle, wall and line add shapes · double-click a shape to edit its corners') };
  const key = b ? JSON.stringify([b, !!drawingEditor.modalDrawEdit]) : '';
  if (key === chromeKey) return;
  chromeKey = key;
  // Folding the catalogue bar away (or back) moves the canvas's left edge: the
  // drawing keeps its place on the screen rather than jumping sideways.
  const cv = $('canvas');
  const left = cv.getBoundingClientRect().left;
  document.body.classList.toggle('drawing-editing', !!b);
  const moved = cv.getBoundingClientRect().left - left;
  if (moved) { vp.tx -= moved; invalidate(); }
  const banner = $('edit-banner');
  banner.classList.toggle('hidden', !b);
  if (!b) return;
  $('edit-banner-crumb').textContent = b.crumb ? `${b.crumb} ›` : '';
  $('edit-banner-title').textContent = b.title;
  $('edit-banner-hint').textContent = b.hint;
  $('edit-banner-done').textContent = tr('Done');
  const cancel = $('edit-banner-cancel');
  cancel.textContent = tr('Cancel');
  cancel.classList.toggle('hidden', !drawingEditor.modalDrawEdit);
}

// ---- Drawing editor ----

/** Sample an arc into a polyline (world/local — same frame as `center`). */
function arcSamplePoints(center: Vec2, radius: number, startDeg: number, endDeg: number): Vec2[] {
  let sweep = endDeg - startDeg;
  while (sweep <= 0) sweep += 360;
  const segs = Math.max(8, Math.ceil((sweep / 360) * 64));
  const out: Vec2[] = [];
  for (let i = 0; i <= segs; i++) {
    const a = ((startDeg + (sweep * i) / segs) * Math.PI) / 180;
    out.push({ x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) });
  }
  return out;
}

/**
 * Convert a product's own imported drawing (`geometry` entities) into editable
 * drawing-editor shapes (`drawPolygons` / `drawWalls`, local frame) so the user
 * can move, resize, vertex-edit and recolour the lines. Runs once per instance on
 * entering the editor; unconvertible entities (text) stay as residual geometry.
 * No-op for rooms and procedurally-drawn categories (their outline isn't geometry).
 */
function promoteGeometryToShapes(inst: ProductInstance): void {
  const g = inst.geometry;
  if (!g || !g.entities.length || inst.autoBenches || traitsOf(inst.category).procedural) return;
  const polys = (inst.drawPolygons ??= []);
  const walls = (inst.drawWalls ??= []);
  const residual: GeomEntity[] = [];
  let seq = 0;
  const uid = (p: string): string => `${newId(p)}-${seq++}`;
  const col = (c?: string): string => (c && /^#[0-9a-fA-F]{6}$/.test(c.slice(0, 7)) ? c.slice(0, 7) : inst.color);
  const addLine = (pts: Vec2[], color: string): void => {
    if (pts.length >= 2) walls.push({ id: uid('dw'), points: pts.map((p) => ({ x: p.x, y: p.y })), style: 'line', thickness: 1, align: 'center', fill: color, fillOpacity: 1 });
  };
  const addPoly = (pts: Vec2[], opt: { ellipse?: boolean; fill?: string; fillOpacity?: number; stroke?: string }): void => {
    // A fill-only shape (a hatch) has no outline in the source geometry, so it
    // must not gain one on entering the editor; an outline shape (closed polyline,
    // circle, ellipse) has a stroke but no fill.
    if (pts.length >= 3) polys.push({ id: uid('dp'), points: pts.map((p) => ({ x: p.x, y: p.y })), ellipse: opt.ellipse || undefined, fill: opt.fill ?? '#000000', fillOpacity: opt.fill ? (opt.fillOpacity ?? 1) : 0, stroke: opt.stroke ?? inst.color, strokeOpacity: opt.fill ? 0 : 1 });
  };
  // A hatch's rendered translucency (see drawEntity): a plain #rrggbb shades at
  // 0.35, a #rrggbbaa carries its own alpha, and an untinted hatch is 25% grey.
  const hatchFill = (c?: string): { fill: string; opacity: number } => {
    if (!c) return { fill: '#9fb3c8', opacity: 0.25 };
    const base = c.slice(0, 7);
    const fill = /^#[0-9a-fA-F]{6}$/.test(base) ? base : inst.color;
    const opacity = c.length === 9 ? parseInt(c.slice(7, 9), 16) / 255 : 0.35;
    return { fill, opacity };
  };
  for (const e of g.entities) {
    switch (e.kind) {
      case 'line': addLine([e.a, e.b], col(e.color)); break;
      case 'polyline': e.closed ? addPoly(e.points, { stroke: col(e.color) }) : addLine(e.points, col(e.color)); break;
      case 'circle': addPoly(ellipsePoints(Math.round(e.center.x), Math.round(e.center.y), Math.round(e.radius), Math.round(e.radius)), { ellipse: true, stroke: col(e.color) }); break;
      case 'arc': addLine(arcSamplePoints(e.center, e.radius, e.startAngle, e.endAngle), col(e.color)); break;
      case 'ellipse':
      case 'spline': { const pts = curveTessellation(e); if (pts && pts.length) (e.closed ? addPoly(pts, { stroke: col(e.color) }) : addLine(pts, col(e.color))); break; }
      case 'hatch': if (e.loops[0] && e.loops[0].length >= 3) { const hf = hatchFill(e.color); addPoly(e.loops[0], { fill: hf.fill, fillOpacity: hf.opacity }); } break;
      default: residual.push(e); // text — not editable as a shape
    }
  }
  inst.geometry = residual.length ? { entities: residual, bounds: g.bounds } : undefined;
}

/** All editor shapes of an instance (polygons + walls), bottom-to-top by `z`. */
export function editorShapesZOrdered(inst: ProductInstance): Array<Polygon | Wall> {
  const polyN = inst.drawPolygons?.length ?? 0;
  const tagged: Array<{ s: Polygon | Wall; z: number }> = [];
  (inst.drawPolygons ?? []).forEach((p, i) => tagged.push({ s: p, z: p.z ?? i }));
  (inst.drawWalls ?? []).forEach((w, i) => tagged.push({ s: w, z: w.z ?? polyN + i }));
  tagged.sort((a, b) => a.z - b.z);
  return tagged.map((t) => t.s);
}

/** Give every editor shape an explicit `z` (bottom-to-top), so polygons and walls
 *  share one well-defined draw stack. Idempotent; preserves the current order. */
function ensureDrawZ(inst: ProductInstance): void {
  editorShapesZOrdered(inst).forEach((s, i) => (s.z = i));
}

/** The next (top-of-stack) `z` for a newly added editor shape. */
export function nextDrawZ(inst: ProductInstance): number {
  let max = -1;
  for (const s of [...(inst.drawPolygons ?? []), ...(inst.drawWalls ?? [])]) if (typeof s.z === 'number') max = Math.max(max, s.z);
  return max + 1;
}

/** Re-stack the selected editor shape (poly or wall) within the shared z-order. */
export function reorderEditorShape(inst: ProductInstance, id: string, op: ZOrderOp): void {
  ensureDrawZ(inst);
  const ordered = editorShapesZOrdered(inst);
  const i = ordered.findIndex((s) => s.id === id);
  if (i < 0) return;
  const target = ordered[i];
  if (op === 'front') target.z = ordered.length; // above current top
  else if (op === 'back') target.z = -1; // below current bottom
  else if (op === 'forward' && i < ordered.length - 1) { const n = ordered[i + 1]; [target.z, n.z] = [n.z, target.z]; }
  else if (op === 'backward' && i > 0) { const p = ordered[i - 1]; [target.z, p.z] = [p.z, target.z]; }
  ensureDrawZ(inst); // renormalise to 0..n-1
}

/** Enter drawing-editor mode for a placed instance. */
export function enterDrawingEdit(instId: string): void {
  const inst = state.drawing.instances.find((i) => i.id === instId);
  // No history: the promotion draws identically, so an undo step here would undo
  // nothing visible — and double-clicking a product is not an edit.
  if (inst && inst.geometry && !inst.autoBenches && !traitsOf(inst.category).procedural) promoteGeometryToShapes(inst);
  if (inst) ensureDrawZ(inst); // one shared draw stack for the arrange buttons
  enterEdit({ kind: 'part', id: instId }); // whatever else was being edited is left, and nothing is picked
  drawingEditor.drawEditVertexDrag = null;
  clearSelection(state);
  setTool('select');
  renderProperties();
  invalidate();
}

/** Exit drawing-editor mode. */
export function exitDrawingEdit(): void {
  if (drawingEditor.modalDrawEdit) {
    closeDrawEditSession(true);
    return;
  }
  leaveEdit('part'); // its picks go with it
  drawingEditor.drawEditVertexDrag = null;
  setTool('select');
  renderProperties();
  invalidate();
}

/** Flatten an instance's editor shapes (plus any residual geometry) back into a
 *  Geometry, so an edited product-modal part keeps its edits. */
function instanceShapesToGeometry(inst: ProductInstance): Geometry {
  const entities: GeomEntity[] = [];
  for (const e of inst.geometry?.entities ?? []) entities.push(structuredClone(e));
  // Emit shapes bottom-to-top so their editor z-order becomes the geometry order
  // (later entities draw on top).
  for (const s of editorShapesZOrdered(inst)) {
    if ('thickness' in s) {
      const w = s as Wall;
      const pts = w.points.map((p) => ({ x: p.x, y: p.y }));
      if (pts.length === 2) entities.push({ kind: 'line', a: pts[0], b: pts[1], color: w.fill });
      else if (pts.length >= 2) entities.push({ kind: 'polyline', points: pts, closed: false, color: w.fill });
      continue;
    }
    const p = s as Polygon;
    const pts = p.points.map((q) => ({ x: q.x, y: q.y }));
    if (pts.length < 3) continue;
    // Bake the fill opacity into the hatch colour (#rrggbbaa) so its translucency
    // survives the round-trip and matches how a hatch renders.
    if (p.fillOpacity > 0) entities.push({ kind: 'hatch', loops: [pts.map((q) => ({ x: q.x, y: q.y }))], solid: true, color: withAlpha(p.fill, p.fillOpacity) });
    if (p.strokeOpacity > 0) entities.push({ kind: 'polyline', points: pts, closed: true, color: p.stroke });
  }
  return { entities, bounds: computeBounds(entities) };
}

/** Open the drawing editor for a product-modal part's own drawing. A throwaway
 *  instance carries the part's geometry onto the canvas; exiting writes it back. */
export function editPartDrawing(part: DrawingHolder): void {
  if (!part.geometry || !part.geometry.entities.length) return;
  const color = npDraft?.color ?? '#cdd6e0';
  const inst: ProductInstance = {
    id: `np-${newId('edit')}`,
    templateId: 'np-edit',
    name: part.name || 'part',
    category: 'custom',
    x: 0,
    y: 0,
    rotation: 0,
    width: part.width,
    depth: part.depth,
    color,
    geometry: { entities: part.geometry.entities.map((e) => structuredClone(e)), bounds: part.geometry.bounds },
    preparations: [],
    properties: [],
  };
  $('modal-backdrop').classList.add('hidden');
  openDrawEditSession(partHost(part), inst, part.geometry.bounds);
}

/** The product editor's part: its shapes written back as its geometry. */
function partHost(part: DrawingHolder): DrawEditHost {
  return {
    finish(inst) {
      const geom = instanceShapesToGeometry(inst);
      if (geom.entities.length && isFiniteBounds(geom.bounds)) {
        const centered = centerGeometry(geom);
        part.geometry = centered;
        part.width = Math.max(1, Math.round(centered.bounds.max.x - centered.bounds.min.x));
        part.depth = Math.max(1, Math.round(centered.bounds.max.y - centered.bounds.min.y));
      } else {
        part.geometry = undefined;
      }
      return null;
    },
    reopen() {
      renderEditor();
      $('modal-backdrop').classList.remove('hidden');
    },
  };
}

/** End the throwaway session: write its shapes back (`apply`, which the host
 *  may refuse), drop its instance and its history, and reopen what opened it. */
function closeDrawEditSession(apply: boolean): void {
  const ctx = drawingEditor.modalDrawEdit!;
  const inst = drawEditInst();
  if (apply && inst) {
    const why = ctx.host.finish(inst);
    if (why) { flashToolStatus(tr(why)); return; }
  }
  leaveEdit('part'); // its picks go with it
  drawingEditor.drawEditVertexDrag = null;
  setTool('select');
  // Restore the real drawing and undo/redo stacks, discarding the editor session.
  undoSteps.unstash(ctx.savedSteps);
  restoreDrawing(ctx.savedDrawing);
  drawingEditor.modalDrawEdit = null;
  // Back where the plan was: the catalogue bar first, then the view as it stood.
  syncDrawEditChrome();
  vp.scale = ctx.view.scale; vp.tx = ctx.view.tx; vp.ty = ctx.view.ty;
  ctx.host.reopen();
  renderProperties();
  invalidate();
}

/** The instance currently open in the drawing editor (or null). */
export function drawEditInst(): ProductInstance | null {
  if (!drawingEditor.drawingEditId) return null;
  return state.drawing.instances.find((i) => i.id === drawingEditor.drawingEditId) ?? null;
}

/** The local shape selected in the drawing editor (a polygon, or a wall / line). */
export function selectedEditorShapeId(): string | null {
  return state.drawEditSelectedPolyId ?? state.drawEditSelectedWallId;
}

/** Delete the drawing editor's selected local shape, one undo step: the Delete
 *  key, Edit ▸ Delete and the toolbar's Delete alike (main.ts SPACES.part) —
 *  none of them reaches the model behind the editor. */
export function deleteEditorShape(): void {
  const inst = drawEditInst(), id = selectedEditorShapeId();
  if (!inst || !id) return;
  pushHistory();
  if (state.drawEditSelectedPolyId) inst.drawPolygons = (inst.drawPolygons ?? []).filter((p) => p.id !== id);
  else inst.drawWalls = (inst.drawWalls ?? []).filter((w) => w.id !== id);
  if (state.drawEditVerticesId === id) state.drawEditVerticesId = null;
  state.drawEditSelectedPolyId = null;
  state.drawEditSelectedWallId = null;
  renderProperties();
  invalidate();
}

/** The local polygon in the editing instance that contains the world point. */
export function drawEditPolyAt(world: Vec2): Polygon | null {
  const inst = drawEditInst();
  if (!inst) return null;
  const local = toInstanceLocal(inst, world);
  for (let i = (inst.drawPolygons?.length ?? 0) - 1; i >= 0; i--) {
    const p = inst.drawPolygons![i];
    if (p.ellipse) {
      const { cx, cy, rx, ry } = ellipseParams(p);
      const dx = (local.x - cx) / Math.max(1, rx), dy = (local.y - cy) / Math.max(1, ry);
      if (dx * dx + dy * dy <= 1) return p;
    } else if (p.points.length >= 3 && polyContains(p, local)) {
      return p;
    }
  }
  return null;
}

/** The local wall/line in the editing instance near the world point. */
export function drawEditWallAt(world: Vec2): Wall | null {
  const inst = drawEditInst();
  if (!inst) return null;
  const local = toInstanceLocal(inst, world);
  const minPick = 10 / vp.scale;
  for (let i = (inst.drawWalls?.length ?? 0) - 1; i >= 0; i--) {
    const w = inst.drawWalls![i];
    const thick = w.style === 'line' ? Math.max(w.thickness, minPick) : w.thickness;
    const band = wallBand(w.points, thick, w.align);
    if (band.length >= 3 && pointInPolygon(local, band)) return w;
  }
  return null;
}

/**
 * In drawing editor mode, the local shape whose vertex handles are visible.
 * Returns { shape, isWall } or null.
 */
export function editingDrawEditShape(): { shape: Polygon | Wall; isWall: boolean } | null {
  if (!state.drawEditVerticesId) return null;
  const inst = drawEditInst();
  if (!inst) return null;
  const poly = inst.drawPolygons?.find((p) => p.id === state.drawEditVerticesId);
  if (poly) return { shape: poly, isWall: false };
  const wall = inst.drawWalls?.find((w) => w.id === state.drawEditVerticesId);
  if (wall) return { shape: wall, isWall: true };
  return null;
}

/** The selected local shape in the drawing editor (poly or wall), or null. */
export function selectedDrawEditShape(): { shape: Polygon | Wall; isWall: boolean } | null {
  const inst = drawEditInst();
  if (!inst) return null;
  if (state.drawEditSelectedPolyId) {
    const p = inst.drawPolygons?.find((s) => s.id === state.drawEditSelectedPolyId);
    if (p) return { shape: p, isWall: false };
  }
  if (state.drawEditSelectedWallId) {
    const w = inst.drawWalls?.find((s) => s.id === state.drawEditSelectedWallId);
    if (w) return { shape: w, isWall: true };
  }
  return null;
}

/**
 * A bounding-box resize handle of the selected editor shape near the screen point,
 * or null. Handles are computed in the instance's local frame (so they rotate with
 * it) and transformed to screen for hit-testing. Hidden while editing vertices.
 */
export function hitDrawEditHandle(sp: Vec2): Handle | null {
  if (state.drawEditVerticesId) return null;
  const sel = selectedDrawEditShape();
  const inst = drawEditInst();
  if (!sel || !inst || sel.shape.points.length < 2) return null;
  for (const h of shapeBBoxHandles(sel.shape.points)) {
    const s = vp.worldToScreen(instanceToWorld(inst, h.world));
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return h;
  }
  return null;
}

/**
 * World-frame copy of a local polygon (all points transformed via instanceToWorld).
 * Used for hit-testing with functions that expect world coordinates.
 */
export function localPolyToWorld(inst: ProductInstance, lp: Polygon): Polygon {
  const toW = (p: Vec2) => instanceToWorld(inst, p);
  return { ...lp, points: lp.points.map(toW), holes: lp.holes?.map((h) => h.map(toW)) };
}

/**
 * World-frame copy of a local wall (all points transformed via instanceToWorld).
 */
export function localWallToWorld(inst: ProductInstance, lw: Wall): Wall {
  return { ...lw, points: lw.points.map((p) => instanceToWorld(inst, p)) };
}
